import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useTranslation, Trans } from 'react-i18next';
import { SupportingDocument } from '../types';
import ApiService from '../services/apiService';
import { logger } from '../utils/logger';

interface SupportingDocumentsProps {
    fileId: string;
    className?: string;
    onDocumentCountChange?: (count: number) => void;
}

// Wat de backend als ondersteunend document accepteert. De server bepaalt het type
// alleen aan de extensie van de bestandsnaam (hoofdletterongevoelig). Afbeeldingen
// zet hij bij het uploaden eenmalig om naar tekst; de afbeelding zelf bewaart hij niet.
const IMAGE_EXTENSIONS = ['png', 'jpg', 'jpeg', 'webp', 'heic', 'heif'];
const ALLOWED_EXTENSIONS = ['pdf', 'doc', 'docx', 'txt', ...IMAGE_EXTENSIONS];
const FILE_INPUT_ACCEPT = ALLOWED_EXTENSIONS.map(ext => `.${ext}`).join(',');

// De proxy voor de server weigert verzoeken boven 10 MB met een 413; deze grens laat
// ruimte voor de multipart-overhead.
const MAX_UPLOAD_BYTES = 10_000_000;

// Die 413 komt zonder CORS-headers en daardoor in de browser vaak binnen als
// netwerkfout zonder status. Bij een bestand boven deze grootte lezen we zo'n fout
// als "te groot"; bij kleinere bestanden is het eerder een echte netwerkfout.
const LIKELY_TOO_LARGE_BYTES = 5_000_000;

const getFileExtension = (filename: string): string => {
    const dot = filename.lastIndexOf('.');
    return dot === -1 ? '' : filename.slice(dot + 1).toLowerCase();
};

const TEXT_CONTENT_TYPES = ['txt', 'text/plain', 'text', 'text/txt'];

const isTextContentType = (contentType: string): boolean =>
    TEXT_CONTENT_TYPES.includes((contentType || '').toLowerCase());

// De vrije notitie ("Opmerking") wordt elders bovenaan getoond als het eerste
// tekstdocument bij de opname. Hier verbergen we datzelfde document zodat het niet
// dubbel verschijnt — ongeacht de bestandsnaam, want de backend kan zelf namen als
// "Meeting-notes-and-context-...txt" of "Handmatige-invoer-...txt" genereren. We
// spiegelen exact de selectie van loadFreeNote() in RecordingDetailPage: het eerste
// document met een tekst-content-type.
const getFreeNoteDocId = (docs: SupportingDocument[]): number | null => {
    const textDoc = docs.find(doc => isTextContentType(doc.content_type));
    return textDoc ? textDoc.id : null;
};

const SupportingDocuments: React.FC<SupportingDocumentsProps> = ({ fileId, className = '', onDocumentCountChange }) => {
    const { t } = useTranslation();
    const [documents, setDocuments] = useState<SupportingDocument[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [isUploading, setIsUploading] = useState(false);
    const [isConvertingImage, setIsConvertingImage] = useState(false);
    const [uploadDescription, setUploadDescription] = useState('');
    const [expandedDocuments, setExpandedDocuments] = useState<Set<number>>(new Set());
    const [showDeleteConfirm, setShowDeleteConfirm] = useState<number | null>(null);
    const [isDragOver, setIsDragOver] = useState(false);
    const [editingDocument, setEditingDocument] = useState<number | null>(null);
    const [editDescription, setEditDescription] = useState('');

    const fileInputRef = useRef<HTMLInputElement>(null);
    const apiService = new ApiService();

    useEffect(() => {
        loadDocuments();
        // eslint-disable-next-line react-hooks/exhaustive-deps -- alleen opnieuw laden bij een ander fileId
    }, [fileId]);

    // Documenten die zichtbaar zijn in de lijst (zonder de vrije notitie).
    const freeNoteDocId = getFreeNoteDocId(documents);
    const visibleDocuments = documents.filter(doc => doc.id !== freeNoteDocId);

    // Notify parent component when document count changes
    useEffect(() => {
        if (onDocumentCountChange) {
            onDocumentCountChange(visibleDocuments.length);
        }
    }, [visibleDocuments.length, onDocumentCountChange]);

    const loadDocuments = async () => {
        try {
            setIsLoading(true);
            const docs = await apiService.getSupportingDocuments(fileId);
            setDocuments(docs);
        } catch (err) {
            setError(t('supportingDocuments.errors.loadFailed'));
            logger.error('Error loading supporting documents:', err);
        } finally {
            setIsLoading(false);
        }
    };

    const getUploadErrorMessage = (err: unknown, file: File, isImage: boolean): string => {
        const uploadFailed = t('supportingDocuments.errors.uploadFailed');
        if (!axios.isAxiosError(err)) return uploadFailed;

        const status = err.response?.status;
        if (status === 413) return t('supportingDocuments.errors.fileTooLarge');
        // Alleen de omzetting van een afbeelding geeft een 502; bij andere bestanden
        // komt die van de proxy en past de afbeeldingsmelding niet.
        if (status === 502 && isImage) return t('supportingDocuments.errors.imageProcessingFailed');
        if (status === 400) {
            const message = (err.response?.data as { error?: unknown } | undefined)?.error;
            return typeof message === 'string' && message ? message : uploadFailed;
        }
        if (!err.response && err.code === 'ERR_NETWORK' && file.size > LIKELY_TOO_LARGE_BYTES) {
            return t('supportingDocuments.errors.fileTooLarge');
        }
        return uploadFailed;
    };

    const uploadFile = async (file: File) => {
        const extension = getFileExtension(file.name);
        if (!ALLOWED_EXTENSIONS.includes(extension)) {
            setError(t('supportingDocuments.errors.invalidFileType'));
            return;
        }
        if (file.size > MAX_UPLOAD_BYTES) {
            setError(t('supportingDocuments.errors.fileTooLarge'));
            return;
        }

        const isImage = IMAGE_EXTENSIONS.includes(extension);
        setIsUploading(true);
        setIsConvertingImage(isImage);
        setError('');

        try {
            const newDoc = await apiService.uploadSupportingDocument(
                fileId,
                file,
                uploadDescription || undefined
            );
            setDocuments(prev => [...prev, newDoc]);
            setUploadDescription('');
        } catch (err) {
            setError(getUploadErrorMessage(err, file, isImage));
            logger.error('Error uploading document:', err);
        } finally {
            setIsUploading(false);
            setIsConvertingImage(false);
        }
    };

    const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        // Leegmaken zodat hetzelfde bestand na een fout opnieuw te kiezen is.
        event.target.value = '';
        if (file) await uploadFile(file);
    };

    const handleDeleteDocument = async (docId: number) => {
        try {
            await apiService.deleteSupportingDocument(docId);
            setDocuments(prev => prev.filter(doc => doc.id !== docId));
            setExpandedDocuments(prev => {
                const newSet = new Set(prev);
                newSet.delete(docId);
                return newSet;
            });
            setShowDeleteConfirm(null);
        } catch (err) {
            setError(t('supportingDocuments.errors.deleteFailed'));
            logger.error('Error deleting document:', err);
        }
    };

    const handleEditDocument = async (docId: number, currentDescription: string) => {
        const docToEdit = documents.find(doc => doc.id === docId);
        if (!docToEdit) return;

        logger.log('Editing document with content_type:', docToEdit.content_type);
        setEditingDocument(docId);

        // Voor tekstbestanden: laad de volledige content
        if (isTextDocument(docToEdit.content_type)) {
            try {
                const docWithContent = await apiService.getSupportingDocumentContent(docId);
                setEditDescription(docWithContent?.content || '');
            } catch (err) {
                setError(t('supportingDocuments.errors.loadContentFailed'));
                logger.error('Error loading document content:', err);
                setEditDescription('');
            }
        } else {
            // Voor andere bestandstypes: alleen beschrijving bewerken
            setEditDescription(currentDescription || '');
        }
    };

    const handleSaveEdit = async (docId: number) => {
        try {
            const docToEdit = documents.find(doc => doc.id === docId);
            if (!docToEdit) return;

            // Voor tekstbestanden: delete het oude document en maak een nieuw aan
            if (isTextDocument(docToEdit.content_type)) {
                // Delete het oude document
                await apiService.deleteSupportingDocument(docId);

                // Maak een nieuw document aan met de bijgewerkte content
                const newDoc = await apiService.addSupportingDocumentContent(
                    fileId,
                    editDescription, // De nieuwe content
                    docToEdit.filename,
                    docToEdit.description || undefined
                );

                // Update de documents lijst - vervang het oude document met het nieuwe
                setDocuments(prev => prev.map(doc =>
                    doc.id === docId ? newDoc : doc
                ));
            } else {
                // Voor andere bestandstypes: update alleen de beschrijving
                const updatedDoc = await apiService.updateSupportingDocument(docId, editDescription);
                setDocuments(prev => prev.map(doc =>
                    doc.id === docId ? { ...doc, description: updatedDoc.description } : doc
                ));
            }

            setEditingDocument(null);
            setEditDescription('');
        } catch (err) {
            setError(t('supportingDocuments.errors.updateFailed'));
            logger.error('Error updating document:', err);
        }
    };

    const handleCancelEdit = () => {
        setEditingDocument(null);
        setEditDescription('');
    };

    const handleToggleDocument = async (docId: number) => {
        const isExpanded = expandedDocuments.has(docId);

        if (isExpanded) {
            setExpandedDocuments(prev => {
                const newSet = new Set(prev);
                newSet.delete(docId);
                return newSet;
            });
        } else {
            try {
                const docWithContent = await apiService.getSupportingDocumentContent(docId);
                if (docWithContent) {
                    // Update het document in de lijst met de content
                    setDocuments(prev => prev.map(doc =>
                        doc.id === docId ? { ...doc, content: docWithContent.content } : doc
                    ));
                    setExpandedDocuments(prev => {
                        const newSet = new Set(prev);
                        newSet.add(docId);
                        return newSet;
                    });
                }
            } catch (err) {
                setError(t('supportingDocuments.errors.loadContentFailed'));
                logger.error('Error loading document content:', err);
            }
        }
    };

    const handleDragOver = (event: React.DragEvent<HTMLLabelElement>) => {
        event.preventDefault();
        event.stopPropagation();
        setIsDragOver(true);
    };

    const handleDragEnter = (event: React.DragEvent<HTMLLabelElement>) => {
        event.preventDefault();
        event.stopPropagation();
        setIsDragOver(true);
    };

    const handleDragLeave = (event: React.DragEvent<HTMLLabelElement>) => {
        event.preventDefault();
        event.stopPropagation();

        // Alleen isDragOver op false zetten als we de drop zone verlaten, niet child elementen
        const rect = event.currentTarget.getBoundingClientRect();
        const x = event.clientX;
        const y = event.clientY;

        if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
            setIsDragOver(false);
        }
    };

    const handleDrop = async (event: React.DragEvent<HTMLLabelElement>) => {
        event.preventDefault();
        event.stopPropagation();
        setIsDragOver(false);

        if (isUploading) return;

        const file = event.dataTransfer.files[0]; // Neem alleen het eerste bestand
        if (file) await uploadFile(file);
    };

    const formatDate = (dateString: string | null | undefined): string => {
        if (!dateString) return t('supportingDocuments.unknownDate');
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return t('supportingDocuments.unknownDate');
            return date.toLocaleDateString('nl-NL', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch {
            return t('supportingDocuments.unknownDate');
        }
    };

    const isTextDocument = (contentType: string): boolean => isTextContentType(contentType);

    const getFileTypeIcon = (contentType: string): string => {
        switch (contentType.toLowerCase()) {
            case 'pdf':
                return '📄';
            case 'doc':
            case 'docx':
                return '📝';
            case 'txt':
            case 'text':
            case 'text/plain':
                return '📋';
            case 'image':
                return '🖼️';
            default:
                return '📄';
        }
    };

    return (
        <div className={`bg-white border border-gray-200 ${className}`}>
            <div className="p-4">
                {/* Compacte header met beschrijving */}
                <div className="mb-4">
                    <p className="text-sm text-gray-600">
                        {t('supportingDocuments.header.description')} {visibleDocuments.length > 0 && t('supportingDocuments.header.addedCount', { count: visibleDocuments.length })}
                    </p>
                </div>

                {error && (
                    <div className="mb-4 bg-red-50 border border-red-200 rounded-md p-3 flex items-start justify-between gap-2">
                        <p className="text-red-600 text-sm">{error}</p>
                        <button
                            type="button"
                            onClick={() => setError('')}
                            className="text-red-400 hover:text-red-600 focus:outline-none"
                            aria-label={t('common.close')}
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                )}

                {/* Compacte upload zone */}
                <div className="space-y-3 mb-4">
                    {/* File upload */}
                    <div>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept={FILE_INPUT_ACCEPT}
                            onChange={handleFileUpload}
                            disabled={isUploading}
                            className="hidden"
                            id="document-file-input"
                        />
                        <label
                            htmlFor="document-file-input"
                            className={`
                                group block w-full border-2 border-dashed rounded-lg text-center transition-all duration-300 cursor-pointer p-4
                                ${isUploading
                                    ? 'border-primary-200 bg-primary-50 cursor-not-allowed'
                                    : isDragOver
                                        ? 'border-primary-400 bg-primary-50'
                                        : 'border-gray-300 bg-gray-50 hover:border-primary-300 hover:bg-primary-50'
                                }
                            `}
                            onDragOver={handleDragOver}
                            onDragEnter={handleDragEnter}
                            onDragLeave={handleDragLeave}
                            onDrop={handleDrop}
                        >
                            <div className="flex items-center justify-center space-x-2">
                                {isUploading ? (
                                    <svg className="animate-spin w-4 h-4 text-primary-600" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                ) : (
                                    <svg className="w-4 h-4 text-gray-400 group-hover:text-primary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                                    </svg>
                                )}
                                <span className="text-sm text-gray-600 group-hover:text-primary-600">
                                    {isUploading
                                        ? isConvertingImage
                                            ? t('supportingDocuments.upload.convertingImage')
                                            : t('supportingDocuments.upload.uploading')
                                        : isDragOver
                                            ? t('supportingDocuments.upload.dropHint')
                                            : t('supportingDocuments.upload.prompt')
                                    }
                                </span>
                            </div>
                            <div className="text-xs text-gray-400 mt-1">
                                {t('supportingDocuments.upload.fileTypes')}
                            </div>
                            <div className="text-xs text-gray-400 mt-0.5">
                                {t('supportingDocuments.upload.imageNote')}
                            </div>
                        </label>
                    </div>
                </div>

                {/* Documents list */}
                {isLoading ? (
                    <div className="flex items-center justify-center py-6">
                        <svg className="animate-spin h-5 w-5 text-primary-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span className="ml-2 text-gray-600 text-sm">{t('supportingDocuments.list.loading')}</span>
                    </div>
                ) : visibleDocuments.length === 0 ? (
                    <div className="text-center py-6">
                        <svg className="mx-auto h-6 w-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        <p className="mt-2 text-sm text-gray-500">
                            {t('supportingDocuments.list.empty')}
                        </p>
                    </div>
                ) : (
                    <div className="space-y-2">
                        {visibleDocuments.map((doc) => (
                            <div key={doc.id} className="border border-gray-200 rounded-md">
                                <div className="p-3">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center flex-1 min-w-0">
                                            <span className="text-lg mr-2">{getFileTypeIcon(doc.content_type)}</span>
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm font-medium text-gray-900 truncate">
                                                    {doc.filename}
                                                </p>
                                                {!isTextDocument(doc.content_type) && (
                                                    <div className="flex items-center text-xs text-gray-500">
                                                        <span>{formatDate(doc.created_at)}</span>
                                                    </div>
                                                )}
                                                {editingDocument === doc.id ? (
                                                    <div className="mt-2 space-y-2">
                                                        {isTextDocument(doc.content_type) ? (
                                                            <textarea
                                                                value={editDescription}
                                                                onChange={(e) => setEditDescription(e.target.value)}
                                                                placeholder={t('supportingDocuments.edit.contentPlaceholder')}
                                                                rows={6}
                                                                className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500 resize-none"
                                                            />
                                                        ) : (
                                                            <input
                                                                type="text"
                                                                value={editDescription}
                                                                onChange={(e) => setEditDescription(e.target.value)}
                                                                placeholder={t('supportingDocuments.edit.descriptionPlaceholder')}
                                                                className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                                                            />
                                                        )}
                                                        <div className="flex items-center space-x-2">
                                                            <button
                                                                onClick={() => handleSaveEdit(doc.id)}
                                                                className="px-3 py-1.5 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700"
                                                            >
                                                                {t('common.save')}
                                                            </button>
                                                            <button
                                                                onClick={handleCancelEdit}
                                                                className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                                                            >
                                                                {t('common.cancel')}
                                                            </button>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    doc.description && (
                                                        <div className="mt-1 text-xs text-gray-600 italic truncate">
                                                            {doc.description}
                                                        </div>
                                                    )
                                                )}
                                            </div>
                                        </div>
                                        <div className="flex items-center space-x-1">
                                            <button
                                                onClick={() => handleToggleDocument(doc.id)}
                                                className="p-1.5 text-gray-400 hover:text-gray-600 rounded"
                                                title={expandedDocuments.has(doc.id) ? t('supportingDocuments.actions.hideContent') : t('supportingDocuments.actions.showContent')}
                                            >
                                                <svg
                                                    className={`w-4 h-4 transform transition-transform ${expandedDocuments.has(doc.id) ? 'rotate-180' : ''}`}
                                                    fill="none"
                                                    stroke="currentColor"
                                                    viewBox="0 0 24 24"
                                                >
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                                </svg>
                                            </button>
                                            <button
                                                onClick={() => handleEditDocument(doc.id, doc.description || '')}
                                                className="p-1.5 text-primary-400 hover:text-primary-600 rounded"
                                                title={t('supportingDocuments.actions.editDescription')}
                                                disabled={editingDocument === doc.id}
                                            >
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                                </svg>
                                            </button>
                                            <button
                                                onClick={() => setShowDeleteConfirm(doc.id)}
                                                className="p-1.5 text-red-400 hover:text-red-600 rounded"
                                                title={t('supportingDocuments.actions.deleteDocument')}
                                            >
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                </svg>
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                {/* Document content */}
                                {expandedDocuments.has(doc.id) && doc.content && (
                                    <div className="border-t border-gray-200 p-3 bg-gray-50">
                                        <div className="max-h-48 overflow-y-auto">
                                            <pre className="text-xs text-gray-700 whitespace-pre-wrap font-sans leading-relaxed">
                                                {doc.content}
                                            </pre>
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Delete Confirmation Dialog */}
            {showDeleteConfirm !== null && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
                    <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
                        <div className="flex items-center mb-4">
                            <div className="flex-shrink-0">
                                <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 19.5c-.77.833.192 2.5 1.732 2.5z" />
                                </svg>
                            </div>
                            <div className="ml-3">
                                <h3 className="text-sm font-medium text-gray-900">
                                    {t('supportingDocuments.deleteDialog.title')}
                                </h3>
                            </div>
                        </div>
                        <div className="mb-4">
                            <p className="text-sm text-gray-500">
                                <Trans
                                    i18nKey="supportingDocuments.deleteDialog.confirmText"
                                    values={{ filename: documents.find(doc => doc.id === showDeleteConfirm)?.filename }}
                                    components={{ strong: <strong /> }}
                                />
                            </p>
                        </div>
                        <div className="flex justify-end space-x-3">
                            <button
                                onClick={() => setShowDeleteConfirm(null)}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                            >
                                {t('common.cancel')}
                            </button>
                            <button
                                onClick={() => showDeleteConfirm && handleDeleteDocument(showDeleteConfirm)}
                                className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                            >
                                {t('common.delete')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SupportingDocuments;